// Briefing templates — localStorage-backed tenant config.
//
// One built-in default seeds the list. The user can edit it, duplicate
// it, create new ones, and pick one as active. The active template
// drives:
//   1. The scaffold sections shown in the Creative Direction / Briefing
//      tabs while the agent is still working
//   2. The structure passed into the agent's system prompt so it tries
//      to emit those exact section keys

import type { BriefingTemplate, TemplateSection } from "./types";

const STORAGE_KEY = "inku.templates";
const ACTIVE_KEY = "inku.activeTemplateId";

const uid = () => Math.random().toString(36).slice(2, 10);

const DEFAULT_TEMPLATE: BriefingTemplate = {
  id: "tpl_default",
  name: "Inku default",
  description:
    "The standard creative-direction brief: vision, audience, tone, claims, mandatories.",
  builtIn: true,
  updatedAt: Date.now(),
  sections: [
    {
      id: "s1",
      heading: "Mission",
      description:
        "The single most important outcome the work needs to drive (audience, business, brand).",
      type: "string",
    },
    {
      id: "s2",
      heading: "Audience",
      description:
        "Who the work is talking to. Demographics, psychographics, where they are in their buying journey.",
      type: "string",
    },
    {
      id: "s3",
      heading: "Tone",
      description:
        "How the work should feel. Voice, cadence, register. What it is, what it's not.",
      type: "string",
    },
    {
      id: "s4",
      heading: "Key claims",
      description:
        "Facts the work is allowed to lean on. Approved messaging, brand-truth statements, product specs.",
      type: "bullets",
    },
    {
      id: "s5",
      heading: "Mandatories",
      description:
        "Things the work MUST do (legal, brand, operational). Hard constraints, not preferences.",
      type: "bullets",
    },
  ],
};

export function loadTemplates(): BriefingTemplate[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as BriefingTemplate[];
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch {
    /* fall through to seed */
  }
  // First load: seed with the built-in default and persist.
  const seed = [DEFAULT_TEMPLATE];
  saveTemplates(seed);
  return seed;
}

export function saveTemplates(templates: BriefingTemplate[]): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(templates));
}

export function loadActiveTemplateId(): string {
  return localStorage.getItem(ACTIVE_KEY) ?? DEFAULT_TEMPLATE.id;
}

export function saveActiveTemplateId(id: string): void {
  localStorage.setItem(ACTIVE_KEY, id);
}

export function getTemplateById(
  templates: BriefingTemplate[],
  id: string | null | undefined
): BriefingTemplate | undefined {
  if (!id) return undefined;
  return templates.find((t) => t.id === id);
}

export function newTemplate(): BriefingTemplate {
  return {
    id: "tpl_" + uid(),
    name: "New template",
    description: "",
    sections: [],
    updatedAt: Date.now(),
  };
}

export function newSection(): TemplateSection {
  return {
    id: "s_" + uid(),
    heading: "New section",
    description: "",
    type: "string",
  };
}

// Serialize the template into a system-prompt fragment the agent can
// consume. Concise, structured, and unambiguous about what to fill.
export function templateToSystemPrompt(t: BriefingTemplate): string {
  if (!t.sections.length) return "";
  const lines: string[] = [
    `When you emit the cd_brief / brief artifact, structure the output to match this template:`,
    `Template: ${t.name}`,
  ];
  if (t.description) lines.push(`Purpose: ${t.description}`);
  lines.push("Sections (order matters; use these exact keys):");
  for (const s of t.sections) {
    const typeHint =
      s.type === "bullets"
        ? "(array of strings)"
        : s.type === "object"
          ? "(nested object with named fields)"
          : "(prose paragraph)";
    lines.push(`  - ${s.heading} ${typeHint}: ${s.description}`);
  }
  return lines.join("\n");
}
